/* eslint-disable indent */
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom'
import { countries } from '../assets/countryList'
import { Button, LoadingSpinner, SearchableSelect, InlineAlert } from '../components/BaseComponents'
import ISICSelector from '../components/BaseComponents/ISICSelector/ISICSelector'
import PageHeadings from '../components/BaseComponents/PageHeadings/PageHeadings'
import PageTemplate from '../components/BaseComponents/PageTemplate/PageTemplate'
import routes from '../components/Router/routes'
import { getSupplier } from '../redux/actions/suppliersActions'
import { updateSupplier } from '../redux/actions/updateSuppliersActions'
import { useAppDispatch, useAppSelector } from '../redux/hooks/reduxHooks'
import {
	selectIndividualSupplier,
	selectSuppliersError,
	selectSuppliersStatus,
	SUPPLIERS_RESET,
} from '../redux/slices/suppliersSlice'
import {
	selectUpdateSuppliersStatus,
	UPDATESUPPLIERS_RESET,
} from '../redux/slices/updateSuppliersSlice'
import { FetchStatus, PostStatus } from '../types/LoadingStates'
import useApi from '../hooks/useApi'
import { multiFieldSubstringFilter } from '../utils/dropdownFilters'

const CompanyEditSupplier = () => {
	const api = useApi();
	const { state } = useLocation()
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const { supplierId } = useParams()

	const suppliersStatus = useAppSelector(selectSuppliersStatus)
	const supplierUpdateStatus = useAppSelector(selectUpdateSuppliersStatus)
	const suppliersError = useAppSelector(selectSuppliersError)
	const supplierData = useAppSelector(selectIndividualSupplier)

	const [newCode, setNewCode] = useState<Array<string> | undefined>(undefined)
	const [newJurisdiction, setNewJurisdiction] = useState<string | undefined | null>(undefined)

	const countriesList = countries


	// Reset supplier data
	useEffect(() => {
		dispatch({
			type: SUPPLIERS_RESET,
		})
		dispatch({
			type: UPDATESUPPLIERS_RESET,
		})
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Data loading - Supplier
	useEffect(() => {
		if (suppliersStatus === FetchStatus.initial) {
			// No supplier data, attempt to load supplier
			dispatch(getSupplier(api, supplierId))
		}

		if (suppliersStatus === FetchStatus.loaded) {
			if (supplierData?.supplierIndustryCode?.code) {
				setNewCode([supplierData?.supplierIndustryCode.code])
			} else {
				setNewCode(undefined)
			}

			if (supplierData?.jurisdiction_code) {
				setNewJurisdiction(supplierData.jurisdiction_code)
			} else {
				setNewJurisdiction(undefined)
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [suppliersStatus, supplierData])

	// Derive whether the form differs from the loaded supplier, to gate the update button
	let jurisdictionValue: string | null | undefined
	let codeValue: string | null | undefined

	if (supplierData?.jurisdiction_code !== newJurisdiction) {
		if (typeof newJurisdiction === 'undefined' && supplierData?.jurisdiction_code !== null) {
			jurisdictionValue = null
		} else {
			jurisdictionValue = newJurisdiction
		}
	}

	if (supplierData?.supplierIndustryCode?.code !== (newCode && newCode[0])) {
		if (typeof newCode === 'undefined') {
			if (supplierData?.supplierIndustryCode !== null) {
				codeValue = null
			}
		} else {
			codeValue = newCode[0]
		}
	}

	const hasChanges = jurisdictionValue !== undefined || codeValue !== undefined

	return (
		<PageTemplate>
			<Helmet>
				<title>{routes.companySupplierEdit.name}</title>
			</Helmet>
			{state === 'carbon' && (
				<PageHeadings
					crumbs={[
						{ label: 'Purchases', route: routes.companyPurchases.path },
						{ label: routes.companyCarbonAccounts.menuName as string, route: generatePath(routes.companyCarbonAccounts.path), routeState: 'suppliers' },
						{ label: 'Edit supplier' },
					]}
				/>
			)}
			{state === null && (
				<PageHeadings
					crumbs={[
						{ label: 'Purchases', route: routes.companyPurchases.path },
						{ label: routes.companySuppliers.menuName as string, route: routes.companySuppliers.path },
						{ label: 'Edit supplier' },
					]}
				/>
			)}

			{suppliersStatus === FetchStatus.loaded && (
				<>
					<dl className='mt-3 grid grid-cols-3 text-white'>
						<dt className='bg-charcoal-400 p-4 border-y border-charcoal-200'>Supplier name</dt>
						<dd className='col-span-2 p-4 border-y border-charcoal-200'>
							{supplierData?.name || '-'}
						</dd>

						<dt className='bg-charcoal-400 p-4 border-y border-charcoal-200'>Organisation number</dt>
						<dd className='col-span-2 p-4 border-y border-charcoal-200'>
							{supplierData?.company_number || '-'}
						</dd>

						<dt className='bg-charcoal-400 p-4 border-b border-charcoal-200'>Jurisdiction</dt>
						<dd className='col-span-2 p-4 border-b border-charcoal-200'>
							{supplierData?.jurisdiction_code || '-'}
						</dd>

						<dt className='bg-charcoal-400 p-4 border-b border-charcoal-200'>Sector Code (ISIC)</dt>
						<dd className='col-span-2 p-4 border-b border-charcoal-200'>
							{supplierData?.supplierIndustryCode?.code || '-'}
							{supplierData?.supplierIndustryCode?.name &&
								` - ${supplierData?.supplierIndustryCode?.name}`}
						</dd>
					</dl>

					<form
						onSubmit={(e) => {
							e.preventDefault()
						}}
					>
						{supplierUpdateStatus !== PostStatus.sent &&
							supplierUpdateStatus !== PostStatus.sending && (
								<>
									<div className='my-4 p-4 bg-charcoal-600 rounded-lg max-w-5xl'>
										<p className='text-white mb-4'>Add/Change the jurisdiction of this supplier:</p>
									<SearchableSelect
										id='countrylist'
										options={countriesList}
										isMulti={false}
										isSearchable={true}
										isClearable={true}
										fillContainer
										className='w-full'
										filterOption={multiFieldSubstringFilter}
										onChange={(value) => {
											setNewJurisdiction(value?.value)
										}}
										value={countriesList.filter(function (option) {
											return option.value === newJurisdiction
										})}
									/>
									</div>

									<div className='my-4 p-4 bg-charcoal-600 rounded-lg max-w-5xl'>
										<p className='text-white mb-4'>
											Add/Change the sector code (ISIC) to represent this supplier:
										</p>

										<ISICSelector
											key={supplierId}
											darkMode={true}
											value={newCode}
											min={1}
											max={1}
											updateHandler={(value) => {
												// TODO: ISIC Selector needs refactor to avoid this horrible logic
												if (value && value.length > 0) {
													if (typeof newCode === 'undefined') {
														setNewCode(value)
													}
													if (newCode && newCode[0] !== value[0]) {
														setNewCode(value)
													}
												} else {
													setNewCode(undefined)
												}
											}}
										/>
									</div>
								</>
							)}

						{supplierUpdateStatus === PostStatus.sending && (
							<div className='my-4 p-4 bg-charcoal-600 rounded-lg'>
								<LoadingSpinner inline loadingText='Updating supplier...' />
							</div>
						)}

						{supplierUpdateStatus === PostStatus.error && (
							<InlineAlert type='error' className='mt-4'>								<p className='text-white'>
									There was an error attempting to update your supplier. Please try again. If the
									problem persists, please contact the Neoni team for support.
								</p>
							</InlineAlert>
						)}

						{supplierUpdateStatus === PostStatus.sent && (
							<>
								<div className='mt-8 mb-4 bg-white rounded-lg p-4'>
									<p>
										<span className='fa-solid fa-check mr-2' />
										Your supplier was successfully updated
									</p>
								</div>

								<Button
									type='Primary'
									onClick={() => {
										navigate(routes.companySuppliers.path)
									}}
									text='Return to suppliers overview'
									hasLeadingIcon
									additionalClass='mt-4'
									icon='fa-solid fa-arrow-left'
								/>
								<Button
									type='Outlined'
									onClick={() => {
										navigate(0)
									}}
									text='Edit'
									hasLeadingIcon
									additionalClass='mt-4 ml-4'
									icon='fa-solid fa-pen-to-square'
								/>
							</>
						)}

						{supplierUpdateStatus === PostStatus.initial && (
							<Button
								type='Primary'
								text='Update supplier'
								hasTrailingIcon
								icon='fa-solid fa-pen-to-square'
								disabled={!hasChanges}
								onClick={() => {
									// Send off the API call if there's a detected change
									if (jurisdictionValue !== undefined || codeValue !== undefined) {
										dispatch(
											updateSupplier(api, {
												supplierId: supplierId,
												jurisdication: jurisdictionValue,
												code: codeValue,
											})
										)
									}
								}}
							/>
						)}
					</form>
				</>
			)}

			{suppliersStatus === FetchStatus.loading && (
				<LoadingSpinner loadingText='Loading supplier...' />
			)}

			{suppliersStatus === FetchStatus.error && (
				<InlineAlert type='error' className='mt-4'>					<p className='text-white mb-4'>
						There was an error whilst trying to fetch this supplier. If this problem persists,
						please contact the Neoni team.
					</p>
					{suppliersError && (
						<p className='text-white mb-4'>Error message: {suppliersError.toString()}</p>
					)}
					<Button
						type='Primary'
						hasLeadingIcon
						additionalClass='mt-4'
						icon='fa-solid fa-arrow-left'
						text='Return to suppliers overview'
						onClick={() => {
							navigate(routes.companySuppliers.path)
						}}
					/>
				</InlineAlert>
			)}
		</PageTemplate>
	)
}

export default CompanyEditSupplier
